/** Handing files to the user, and taking one back. */

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick so the download has taken the reference.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadText(filename: string, text: string, mime: string): void {
  downloadBlob(filename, new Blob([text], { type: `${mime};charset=utf-8` }));
}

export interface PickedFile {
  readonly name: string;
  readonly text: string;
  readonly bytes: number;
}

/** Reads a JSON file the user picked. Contents are never logged. */
export function pickJsonFile(maxBytes: number): Promise<PickedFile | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.style.display = 'none';
    document.body.appendChild(input);

    const cleanup = () => input.remove();

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) {
        cleanup();
        resolve(null);
        return;
      }
      // Size is checked before reading, so an enormous file is never held in memory.
      if (file.size > maxBytes) {
        cleanup();
        reject(
          new Error(
            `That file is ${(file.size / 1_048_576).toFixed(1)} MB, over the ${(
              maxBytes / 1_048_576
            ).toFixed(0)} MB import limit.`,
          ),
        );
        return;
      }
      const reader = new FileReader();
      reader.onerror = () => {
        cleanup();
        reject(new Error('That file could not be read.'));
      };
      reader.onload = () => {
        cleanup();
        const text = typeof reader.result === 'string' ? reader.result : '';
        resolve({ name: file.name, text, bytes: file.size });
      };
      reader.readAsText(file);
    });

    input.addEventListener('cancel', () => {
      cleanup();
      resolve(null);
    });

    input.click();
  });
}
